import { motion } from 'framer-motion';
import { MessageCircle, ArrowRight, Play } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import SenteticaHeroText from './SenteticaHeroText';

const HeroSection = ({ whatsappUrl }) => {
  const { language } = useLanguage();

  const content = {
    en: {
      badge: 'Digital Intelligence Solutions',
      headline: 'A new era begins with AI.',
      subheadline: 'Transform your brand with intelligent marketing solutions that deliver measurable results.',
      cta: 'Get Free Consultation',
      viewWork: 'View Our Work',
      clients: '400+ Clients Worldwide',
      trustedBy: 'Trusted by leading brands',
      dragHint: 'Drag the logo to interact',
    },
    tr: {
      badge: 'Dijital Zeka Çözümleri',
      headline: 'AI ile yeni bir dönem başlıyor.',
      subheadline: 'Ölçülebilir sonuçlar sunan akıllı pazarlama çözümleriyle markanızı dönüştürün.',
      cta: 'Ücretsiz Danışmanlık',
      viewWork: 'Çalışmalarımız',
      clients: '400+ Dünya Genelinde Müşteri',
      trustedBy: 'Lider markaların güveni',
      dragHint: 'Logoyu sürükleyerek etkileşime geçin',
    },
  };

  const t = content[language] || content.en;

  return (
    <section className="relative min-h-screen flex flex-col overflow-hidden bg-white pt-24">
      {/* Premium ambient backdrop — Apple/Stripe style glassmorphism */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* Soft gradient base */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(120% 80% at 50% 0%, rgba(231,236,255,0.55) 0%, rgba(248,245,255,0.35) 40%, rgba(255,255,255,1) 75%)',
          }}
        />

        {/* Floating glassmorphism orbs */}
        <motion.div
          aria-hidden="true"
          className="absolute -top-32 -left-24 w-[520px] h-[520px] rounded-full blur-[120px]"
          style={{ background: 'radial-gradient(circle at 30% 30%, rgba(139,160,255,0.45), rgba(139,160,255,0) 70%)' }}
          animate={{ x: [0, 30, 0], y: [0, 20, 0] }}
          transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          aria-hidden="true"
          className="absolute top-20 right-[-120px] w-[600px] h-[600px] rounded-full blur-[140px]"
          style={{ background: 'radial-gradient(circle at 60% 40%, rgba(196,165,255,0.42), rgba(196,165,255,0) 70%)' }}
          animate={{ x: [0, -25, 0], y: [0, 30, 0] }}
          transition={{ duration: 22, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          aria-hidden="true"
          className="absolute bottom-[-100px] left-1/3 w-[460px] h-[460px] rounded-full blur-[110px]"
          style={{ background: 'radial-gradient(circle at 50% 50%, rgba(170,205,255,0.4), rgba(170,205,255,0) 70%)' }}
          animate={{ x: [0, 20, 0], y: [0, -15, 0] }}
          transition={{ duration: 26, repeat: Infinity, ease: 'easeInOut' }}
        />

        {/* Subtle dot grid for tech texture */}
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: 'radial-gradient(circle, #6b7280 1px, transparent 1px)',
            backgroundSize: '28px 28px',
          }}
        />

        {/* Final white feather at the bottom for clean transition */}
        <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-b from-transparent to-white" />
      </div>

      {/* Animated Sentetica typography — Apple/Stripe minimalism */}
      <div className="relative w-full h-[58vh] md:h-[62vh]">
        <SenteticaHeroText />
      </div>

      {/* Text content overlay */}
      <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 pb-16 pt-6 md:pt-8 w-full">
        <div className="text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Badge */}
            <motion.span
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="inline-block text-[#E60000] text-sm uppercase tracking-[0.2em] font-bold mb-6"
            >
              {t.badge}
            </motion.span>

            {/* Main Headline */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="font-heading text-4xl md:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] text-[#0A0A0A] mb-6"
            >
              {t.headline}
            </motion.h1>

            {/* Subheadline */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-lg md:text-xl text-[#6B7280] leading-relaxed mb-10 max-w-2xl mx-auto"
            >
              {t.subheadline}
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="flex flex-col sm:flex-row justify-center gap-4 mb-12"
            >
              <motion.a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-3 bg-[#E60000] text-white px-8 py-4 rounded-full font-bold text-base shadow-[0_8px_30px_-4px_rgba(230,0,0,0.4)] hover:shadow-[0_12px_40px_-4px_rgba(230,0,0,0.5)] hover:bg-[#CC0000] transition-all duration-300 hover:-translate-y-1"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                data-testid="hero-whatsapp-btn"
              >
                <MessageCircle size={20} />
                {t.cta}
                <ArrowRight size={18} />
              </motion.a>

              <motion.a
                href="#portfolio"
                className="inline-flex items-center justify-center gap-2 border-2 border-[#E5E7EB] text-[#0A0A0A] px-8 py-4 rounded-full font-bold hover:border-[#E60000] hover:text-[#E60000] transition-all duration-300"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                data-testid="hero-portfolio-btn"
              >
                <Play size={18} />
                {t.viewWork}
              </motion.a>
            </motion.div>

            {/* Trust indicator */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="text-center"
            >
              <p className="font-bold text-[#E60000] text-lg">{t.clients}</p>
              <p className="text-[#E60000]/80 text-sm mt-1">{t.trustedBy}</p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
